// Gift Registry Module
// Manages gift product listings in a similar pattern to the RSVP custom form builder.

let giftItemCounter = 0;

// ─── Helpers ────────────────────────────────────────────────────────────────

// Returns the visible element among all elements sharing the given id.
// Falls back to the first match so the sidebar flow continues to work.
function getActiveEl(id) {
    const els = document.querySelectorAll('[id="' + id + '"]');
    for (const el of els) {
        if (el.offsetParent !== null) return el;
    }
    return els[0] || null;
}

function updateGiftItemCount() {
    const container = getActiveEl('giftItemsContainer');
    const badge     = getActiveEl('giftItemCount');
    if (!container || !badge) return;

    const count = container.querySelectorAll('.gift-item').length;
    badge.textContent = count;
    badge.style.display = count > 0 ? 'inline-block' : 'none';

    const emptyState = container.previousElementSibling;
    if (emptyState && emptyState.classList.contains('items-empty-state')) {
        emptyState.style.display = count > 0 ? 'none' : 'flex';
    }
}

/**
 * Resolve an existing image URL for a gift item.
 * Checks storedInvitationData.data.files for a server-persisted URL.
 */
function resolveGiftImageUrl(imageKey) {
    try {
        const sd = window.storedInvitationData;
        if (sd && sd.success && sd.data && sd.data.files && sd.data.files[imageKey]) {
            return sd.data.files[imageKey]; // full URL e.g. http://…/media/invitation_files/abc.jpg
        }
    } catch (e) {}
    return null;
}

// ─── Add / Remove ────────────────────────────────────────────────────────────

function addGiftItem(itemData = null) {
    const container = getActiveEl('giftItemsContainer');
    if (!container) return;

    giftItemCounter++;
    const giftId    = itemData?.id    || `gift_item_${giftItemCounter}`;
    const name      = itemData?.name  || '';
    const price     = itemData?.price || '';
    const url       = itemData?.url   || '';
    const note      = itemData?.note  || '';
    const imageKey  = itemData?.image_key || `gift_img_${giftId}`;

    const existingImgUrl = resolveGiftImageUrl(imageKey);
    const imgPreviewSrc  = existingImgUrl || '';

    const html = `
        <div class="gift-item" data-gift-id="${giftId}" draggable="true">

            <div class="gift-item-header">
                <span class="gift-drag-handle" title="Drag to reorder"><i class="bi bi-grip-vertical"></i></span>
                <button type="button" class="gift-delete-btn" onclick="window.removeGiftItem('${giftId}')">
                    <i class="bi bi-trash"></i>
                </button>
            </div>

            <div class="gift-item-body">

                <div class="gift-item-image-col">
                    <div class="gift-item-image-wrap" onclick="window.openGiftImagePicker('${giftId}')">
                        <div id="giftImgPreview_${giftId}" class="gift-img-inner">
                            ${imgPreviewSrc
                                ? `<img src="${imgPreviewSrc}" class="gift-img-preview" alt="Product image">`
                                : `<div class="gift-img-empty"><i class="bi bi-camera"></i><span>Add photo</span></div>`
                            }
                        </div>
                        <button type="button" class="gift-img-remove-btn"
                                id="giftImgRemoveBtn_${giftId}"
                                style="display:${imgPreviewSrc ? 'flex' : 'none'};"
                                onclick="event.stopPropagation();window.removeGiftItemImage('${giftId}')"
                                title="Remove photo">
                            <i class="bi bi-x"></i>
                        </button>
                    </div>
                    <input type="text"
                           id="giftImgInput_${giftId}"
                           class="d-none media-file-input gift-item-image-input"
                           data-theme-uid="${imageKey}"
                           data-media-type="image"
                           data-file-multiple="false"
                           data-file-action="upload">
                </div>

                <div class="gift-item-inputs-col">
                    <div class="gift-field">
                        <i class="bi bi-tag gift-field-icon"></i>
                        <input type="text"
                               class="gift-clean-input gift-item-name"
                               placeholder="Product name"
                               value="${name}"
                               onchange="window.saveGiftRegistryData()">
                    </div>
                    <div class="gift-row-two">
                        <div class="gift-field">
                            <i class="bi bi-currency-rupee gift-field-icon"></i>
                            <input type="text"
                                   class="gift-clean-input gift-item-price"
                                   placeholder="Price"
                                   value="${price}"
                                   onchange="window.saveGiftRegistryData()">
                        </div>
                        <div class="gift-field">
                            <i class="bi bi-link-45deg gift-field-icon"></i>
                            <input type="url"
                                   class="gift-clean-input gift-item-url"
                                   placeholder="Link"
                                   value="${url}"
                                   onchange="window.saveGiftRegistryData()">
                        </div>
                    </div>
                    <div class="gift-field gift-field-textarea">
                        <i class="bi bi-chat-left-text gift-field-icon"></i>
                        <textarea class="gift-clean-input gift-item-note"
                                  placeholder="Note (optional)"
                                  rows="2"
                                  onchange="window.saveGiftRegistryData()">${note}</textarea>
                    </div>
                </div>

            </div>
        </div>
    `;

    container.insertAdjacentHTML('beforeend', html);

    // After the element is in the DOM, wire up change listener on the proxy input
    // so the remove button becomes visible once the media picker confirms a file.
    const proxyInput = document.getElementById(`giftImgInput_${giftId}`);
    if (proxyInput) {
        proxyInput.addEventListener('change', function () {
            const btn = document.getElementById(`giftImgRemoveBtn_${giftId}`);
            if (btn) btn.style.display = 'block';
            saveGiftRegistryData();
        });
    }

    updateGiftItemCount();
    saveGiftRegistryData();
}

window.addGiftItem = addGiftItem;

window.removeGiftItem = function removeGiftItem(giftId) {
    const el = document.querySelector(`[data-gift-id="${giftId}"]`);
    if (!el) return;

    el.style.opacity = '0';
    el.style.transform = 'translateX(-20px)';
    setTimeout(() => {
        el.remove();
        updateGiftItemCount();
        saveGiftRegistryData();
    }, 300);
};

/** Opens the media picker (from the media library) for a gift item's image slot */
window.openGiftImagePicker = function openGiftImagePicker(giftId) {
    const inputEl   = document.getElementById(`giftImgInput_${giftId}`);
    const previewEl = document.getElementById(`giftImgPreview_${giftId}`);
    if (!inputEl || typeof window.openMediaPicker !== 'function') return;
    window.openMediaPicker(inputEl, previewEl);
};

/** Remove the selected/stored image from a gift item */
window.removeGiftItemImage = function removeGiftItemImage(giftId) {
    const previewEl = document.getElementById(`giftImgPreview_${giftId}`);
    const removeBtn = document.getElementById(`giftImgRemoveBtn_${giftId}`);
    const imgProxy  = document.getElementById(`giftImgInput_${giftId}`);

    if (previewEl) {
        previewEl.innerHTML = `<div class="gift-img-empty"><i class="bi bi-camera"></i><span>Add photo</span></div>`;
    }
    if (removeBtn) removeBtn.style.display = 'none';
    if (imgProxy) {
        imgProxy._selectedFiles = null;
        imgProxy.value = '';
        imgProxy.setAttribute('data-file-action', 'delete');
    }
    saveGiftRegistryData();
};

// ─── Save / Load ─────────────────────────────────────────────────────────────

function saveGiftRegistryData() {
    if (!window.formDataStorage) return;

    const items = [];
    const container = getActiveEl('giftItemsContainer');
    if (!container) return;
    container.querySelectorAll('.gift-item').forEach(el => {
        const giftId    = el.getAttribute('data-gift-id');
        const name      = (el.querySelector('.gift-item-name')  || {}).value || '';
        const price     = (el.querySelector('.gift-item-price') || {}).value || '';
        const url       = (el.querySelector('.gift-item-url')   || {}).value || '';
        const note      = (el.querySelector('.gift-item-note')  || {}).value || '';
        const imageKey  = `gift_img_${giftId}`;

        items.push({ id: giftId, name, price, url, note, image_key: imageKey });
    });

    window.formDataStorage.saveFormData({ gift_registry_items: items });
}

window.saveGiftRegistryData = saveGiftRegistryData;

function loadGiftRegistryData() {
    if (!window.formDataStorage) return;

    const stored = window.formDataStorage.getStoredData();
    const items  = stored?.gift_registry_items;

    if (!Array.isArray(items) || items.length === 0) return;

    const container = getActiveEl('giftItemsContainer');
    if (!container) return;

    // Clear any existing items before loading
    container.innerHTML = '';
    giftItemCounter = 0;

    items.forEach(item => addGiftItem(item));
}

window.loadGiftRegistryData = loadGiftRegistryData;

// ─── Drag & Drop ─────────────────────────────────────────────────────────────

function setupGiftDragAndDrop() {
    const container = getActiveEl('giftItemsContainer');
    if (!container) return;

    let draggedItem = null;

    container.addEventListener('dragstart', function (e) {
        draggedItem = e.target.closest('.gift-item');
        if (draggedItem) {
            draggedItem.classList.add('sortable-drag');
            draggedItem.style.opacity = '0.5';
            e.dataTransfer.effectAllowed = 'move';
        }
    });

    container.addEventListener('dragend', function () {
        if (draggedItem) {
            draggedItem.classList.remove('sortable-drag');
            draggedItem.style.opacity = '1';
            draggedItem = null;
            saveGiftRegistryData();
        }
    });

    container.addEventListener('dragover', function (e) {
        e.preventDefault();
        const after = getGiftDragAfterElement(container, e.clientY);
        const dragging = container.querySelector('.sortable-drag');
        if (!dragging) return;
        if (after == null) {
            container.appendChild(dragging);
        } else {
            container.insertBefore(dragging, after);
        }
    });

    container.addEventListener('touchstart', function (e) {
        const handle = e.target.closest('.gift-drag-handle');
        if (!handle) return;
        e.preventDefault();
        draggedItem = handle.closest('.gift-item');
        if (draggedItem) {
            draggedItem.classList.add('sortable-drag');
            draggedItem.style.opacity = '0.5';
        }
    }, { passive: false });

    container.addEventListener('touchmove', function (e) {
        if (!draggedItem) return;
        e.preventDefault();
        const after = getGiftDragAfterElement(container, e.touches[0].clientY);
        if (after == null) {
            container.appendChild(draggedItem);
        } else {
            container.insertBefore(draggedItem, after);
        }
    }, { passive: false });

    container.addEventListener('touchend', function () {
        if (draggedItem) {
            draggedItem.classList.remove('sortable-drag');
            draggedItem.style.opacity = '1';
            draggedItem = null;
            saveGiftRegistryData();
        }
    });
}

function getGiftDragAfterElement(container, y) {
    const items = [...container.querySelectorAll('.gift-item:not(.sortable-drag)')];
    return items.reduce((closest, child) => {
        const box    = child.getBoundingClientRect();
        const offset = y - box.top - box.height / 2;
        if (offset < 0 && offset > closest.offset) {
            return { offset, element: child };
        }
        return closest;
    }, { offset: Number.NEGATIVE_INFINITY }).element;
}

// ─── Init ──────────────────────────────────────────────────────────────────

window.setupGiftRegistryHandlers = function setupGiftRegistryHandlers() {
    setTimeout(() => {
        // Wire up the "Add Product" button
        const addBtn = document.getElementById('addGiftItem');
        if (addBtn) {
            // Replace node to avoid duplicate listeners
            const fresh = addBtn.cloneNode(true);
            addBtn.parentNode.replaceChild(fresh, addBtn);
            fresh.addEventListener('click', () => addGiftItem());
        }

        // Wire up the Save button in the gift-registry-form-tab
        const giftRegistryForm = document.querySelector('[data-model="gift-registry-form-tab"] form.edit-form');
        if (giftRegistryForm) {
            giftRegistryForm.addEventListener('submit', function (e) {
                e.preventDefault();
                saveGiftRegistryData();

                // Navigate back to features tab
                const backBtn = document.querySelector('[data-model="gift-registry-form-tab"] .edit-form-back-btn');
                if (backBtn) backBtn.click();
            });
        }

        // Load previously saved gift items
        loadGiftRegistryData();
        setupGiftDragAndDrop();
    }, 200);
};

// Legacy stubs kept for safety (nothing in dom-ready.js calls these)
let giftRegistryCounter = 0;
let customGiftCounter = 0;